// Provider: the protocol client (plugin spec §Core engine; protocol spec).
// Owns the WebSocket lifecycle over a NetPort — auth handshake, the batched
// subscribe set, LIVE/BACKGROUND promotion, reconnect with full-jitter
// backoff, ping/pong verification of a socket that may be half-open, proactive
// token re-mint at 80% TTL, and seamless reconnect on TOKEN_EXPIRED. It
// encodes/decodes the protobuf envelope and routes decoded server messages to
// the DocStore/Bridge through the callback surface.

import {
  ClientMessage,
  ServerMessage,
  Mode,
  Role,
  Reason,
  CloseReason,
  type Subscription,
} from "@cairn/proto";
import type { ClockPort, NetPort, SyncSocket } from "./ports";
import { WS_SUBPROTOCOL } from "./ports";
import type { SyncStatus } from "./events";
import { tokenExpiryMs } from "./token";

/**
 * The server did not accept the `cairn-proto.1` WS subprotocol (protocol spec
 * §Versioning: "clients request it and MUST fail loudly if the server doesn't
 * accept"). A version skew reconnecting can't fix, so the Provider surfaces it
 * as a fatal error and stops — never a silent downgrade. Exposed as a typed
 * error so callers/tests can assert the failure mode explicitly.
 */
export class SubprotocolError extends Error {
  constructor(readonly negotiated: string) {
    super(
      `server did not accept the ${WS_SUBPROTOCOL} WS subprotocol ` +
        `(negotiated ${negotiated === "" ? "none" : `"${negotiated}"`})`,
    );
    this.name = "SubprotocolError";
  }
}

/** Backoff/refresh knobs (protocol spec §Reconnection). Overridable in tests. */
export interface ProviderTuning {
  backoffBaseMs: number; // 500
  backoffCapMs: number; // 20_000
  backoffResetMs: number; // reset attempt after a connection survives this
  refreshAtFraction: number; // 0.8 of TTL
  /** How long `verifyLiveness()` waits for the Pong before declaring the
   *  socket half-open and reconnecting. */
  livenessTimeoutMs: number; // 5_000
  /** Deterministic jitter in [0,1) for tests; defaults to Math.random. */
  jitter: () => number;
}

const DEFAULT_TUNING: ProviderTuning = {
  backoffBaseMs: 500,
  // Cap the worst-case idle-between-attempts at 20s (was 60s). After a long
  // outage the delay grows toward this cap, so on the server's return a client
  // with no connectivity signal waits at most ~this long before its next try
  // (protocol spec §Reconnection). The `wake()` path collapses that wait to
  // zero when the OS/app reports connectivity back.
  backoffCapMs: 20_000,
  backoffResetMs: 30_000,
  refreshAtFraction: 0.8,
  // A resumed app's probe crosses a network that may still be settling, so
  // this is a "clearly dead" threshold rather than a latency budget: long
  // enough that a slow mobile round trip isn't mistaken for a corpse, short
  // enough that the user isn't left staring at a socket that syncs nothing.
  livenessTimeoutMs: 5_000,
  jitter: Math.random,
};

export interface ProviderOptions {
  /** `wss://<host>/sync/<vault-id>` — no token in the URL (first-frame auth). */
  wsUrl: string;
  /** Mints a fresh sync token (called at connect and at each refresh). */
  mintToken: () => Promise<string>;
  tuning?: Partial<ProviderTuning>;
}

/** The Provider's view of a subscribed doc (supplied by the DocStore). */
export interface SubscribeParams {
  /** Current Yjs state vector (v1); empty = full sync. */
  stateVector: Uint8Array;
  /** Last acked epoch, or 0 if none (protocol spec §Epochs). */
  knownEpoch: number;
}

/** Everything the Provider reports up to the DocStore/Bridge. */
export interface ProviderCallbacks {
  onAuthAck(userId: string, role: Role): void;
  onStatus(status: SyncStatus, detail?: string): void;
  onSubscribeAck(docId: string, epoch: number, epochChanged: boolean): void;
  onDocPayload(docId: string, payload: Uint8Array): void;
  onRejected(docId: string, reason: Reason, detail: string): void;
  onDocCreated(docId: string): void;
  onDocRemoved(docId: string): void;
  onRoleChanged(role: Role): void;
  /** The Provider asks for a doc's SV + epoch when (re)subscribing. */
  subscribeParams(docId: string): SubscribeParams;
}

/** Subscribe batch cap (protocol spec limits table). */
const MAX_BATCH = 512;

/**
 * Full-jitter exponential backoff (protocol spec §Reconnection): a delay
 * uniformly in [0, min(cap, base·2^attempt)]. Exported so it's unit-testable
 * independently of the socket machinery.
 */
export function fullJitterBackoff(
  attempt: number,
  base: number,
  cap: number,
  rng: () => number = Math.random,
): number {
  const ceiling = Math.min(cap, base * 2 ** attempt);
  return Math.floor(rng() * ceiling);
}

export class Provider {
  private readonly tuning: ProviderTuning;
  private readonly cb: ProviderCallbacks;

  private socket: SyncSocket | null = null;
  private authed = false;
  private closed = false;
  private connecting = false;
  /** Bumped every connection attempt; stale socket callbacks check it. */
  private generation = 0;
  private attempt = 0;
  private connectedAt = 0;
  private role: Role = Role.ROLE_UNSPECIFIED;
  private userId = "";
  private status: SyncStatus = "offline";

  /** Desired subscription set: docId → mode. */
  private readonly desired = new Map<string, Mode>();
  /** Docs whose subscribe frame is queued for the next microtask flush. */
  private pendingSubs: string[] = [];
  private flushScheduled = false;

  /** Reason the current/last socket closed, captured from a Close frame. */
  private lastClose: CloseReason | null = null;
  /** Set when a fatal, non-retryable condition (subprotocol mismatch) occurs. */
  private fatalDetail: string | null = null;
  /** Cancels the pending refresh timer across reconnects. */
  private refreshGen = 0;
  /** Nonce of the outstanding `verifyLiveness()` probe, or null when none. */
  private probeNonce: number | null = null;
  /** Source of probe nonces (monotonic for the life of the Provider). */
  private probeSeq = 0;

  constructor(
    private readonly net: NetPort,
    private readonly clock: ClockPort,
    private readonly opts: ProviderOptions,
    callbacks: ProviderCallbacks,
  ) {
    this.tuning = { ...DEFAULT_TUNING, ...opts.tuning };
    this.cb = callbacks;
  }

  get currentStatus(): SyncStatus {
    return this.status;
  }
  get currentRole(): Role {
    return this.role;
  }
  get currentUserId(): string {
    return this.userId;
  }
  /** Editor+ may send write frames; viewers must not (server READ_ONLY). */
  canWrite(): boolean {
    return this.role === Role.ROLE_EDITOR || this.role === Role.ROLE_OWNER;
  }
  isLive(): boolean {
    return this.authed && this.socket !== null;
  }

  /** Begin connecting (idempotent). */
  connect(): void {
    if (this.closed || this.connecting || this.isLive()) return;
    this.startConnect();
  }

  /**
   * The host observed that connectivity was likely regained — the OS reported
   * the network back online, or the app/window regained focus (see the adapter
   * wiring). If we're currently between reconnect attempts (backing off), reset
   * the attempt counter and reconnect NOW rather than waiting out the remaining
   * backoff, which after a long outage can be as long as the cap. `startConnect`
   * bumps the generation, which neutralizes the pending backoff timer, so there
   * is no double-connect.
   *
   * A no-op when already live, mid-connect, permanently closed, or parked in a
   * fatal error state (AUTH_FAILED / KICKED / subprotocol mismatch) — those
   * either don't need a nudge or intentionally never auto-reconnect. Only the
   * backing-off "offline" state is woken.
   */
  wake(): void {
    if (this.closed || this.connecting || this.isLive()) return;
    if (this.status !== "offline") return;
    this.attempt = 0;
    this.startConnect();
  }

  /**
   * Prove the connection is actually alive, or replace it. Called when the host
   * comes back to the foreground (see the adapter wiring): a suspended app's
   * socket is routinely torn down by the OS or an intermediary WITHOUT a close
   * frame ever arriving, so `isLive()` keeps answering true for a socket that
   * carries nothing — half-open. Only an application-level round trip tells the
   * two apart, so we send a Ping and give the Pong `livenessTimeoutMs`; a missed
   * Pong means dead, and we drop the socket and reconnect at once
   * (decisions/99-mobile-support.md §Lifecycle).
   *
   * A no-op when NOT live: there is no socket to doubt, and the connect/backoff
   * path (`connect()` / `wake()`) already owns getting one — this must not grow
   * a second, competing way to open a socket.
   *
   * Also a no-op while a probe is already outstanding, so repeated resume
   * signals (visibility + focus can fire together) don't stack timers.
   */
  verifyLiveness(): void {
    if (this.closed || !this.isLive()) return;
    if (this.probeNonce !== null) return;
    const nonce = ++this.probeSeq;
    const gen = this.generation;
    this.probeNonce = nonce;
    this.ping(nonce);
    void this.clock.timer(this.tuning.livenessTimeoutMs).then(() => {
      // Generation moved on (reconnect elsewhere) or the Pong landed and
      // cleared the nonce → nothing to do.
      if (this.closed || gen !== this.generation) return;
      if (this.probeNonce !== nonce) return;
      this.probeNonce = null;
      this.replaceSocket();
    });
  }

  /** Add or update a desired subscription; batched to the server. */
  subscribe(docId: string, mode: Mode): void {
    this.desired.set(docId, mode);
    if (this.authed) this.queueSubscribe(docId);
  }

  /** Promote/demote a subscribed doc between LIVE and BACKGROUND. */
  setMode(docId: string, mode: Mode): void {
    const prev = this.desired.get(docId);
    this.desired.set(docId, mode);
    if (this.authed && prev !== undefined) {
      this.sendMessage({ setMode: { docId, mode } });
    }
  }

  /** Drop a subscription. */
  unsubscribe(docId: string): void {
    this.desired.delete(docId);
    if (this.authed) this.sendMessage({ unsubscribe: { docIds: [docId] } });
  }

  /**
   * Send a `DocPayload` frame (a sync step reply or an incremental update).
   * Returns false when not live — the caller keeps the update in its Y.Doc
   * and it resyncs via the state-vector exchange on reconnect.
   */
  sendDoc(docId: string, payload: Uint8Array): boolean {
    if (!this.authed || !this.socket) return false;
    this.sendMessage({ doc: { docId, payload } });
    return true;
  }

  /** Liveness ping (server echoes the nonce back in a Pong). The Provider's
   *  own use is `verifyLiveness()`, which times the answer. */
  ping(nonce: number): void {
    if (this.authed) this.sendMessage({ ping: { nonce } });
  }

  /** Permanent shutdown. No further reconnects. */
  close(): void {
    this.closed = true;
    this.refreshGen++;
    this.socket?.close();
    this.socket = null;
    this.authed = false;
    this.setStatus("offline");
  }

  // --- connection machinery ------------------------------------------------

  private setStatus(status: SyncStatus, detail?: string): void {
    if (status === this.status && detail === undefined) return;
    this.status = status;
    this.cb.onStatus(status, detail);
  }

  private startConnect(): void {
    if (this.closed) return;
    // Clear any prior fatal condition so an explicit reconnect (e.g. after the
    // server is upgraded) re-evaluates the handshake from scratch.
    this.fatalDetail = null;
    this.connecting = true;
    const gen = ++this.generation;
    this.setStatus("connecting");
    this.opts
      .mintToken()
      .then((token) => {
        if (this.closed || gen !== this.generation) return;
        this.openSocket(token, gen);
      })
      .catch((e: unknown) => {
        if (this.closed || gen !== this.generation) return;
        this.connecting = false;
        this.scheduleReconnect(`token mint failed: ${String(e)}`);
      });
  }

  private openSocket(token: string, gen: number): void {
    const socket = this.net.connect(this.opts.wsUrl);
    this.socket = socket;
    this.lastClose = null;
    socket.onOpen(() => {
      if (gen !== this.generation) return;
      // The server MUST have selected `cairn-proto.1` (protocol spec
      // §Versioning). A lenient server that accepts the socket without echoing
      // the subprotocol is a version skew we refuse to run against — fail
      // loudly here rather than proceed to Auth on an unversioned transport.
      const negotiated = socket.protocol();
      if (negotiated !== WS_SUBPROTOCOL) {
        this.failFatal(socket, new SubprotocolError(negotiated));
        return;
      }
      // First frame MUST be Auth within 5 s (protocol spec §lifecycle).
      this.rawSend(socket, { auth: { syncToken: token } });
    });
    socket.onMessage((frame) => {
      if (gen !== this.generation) return;
      this.handleServer(frame, gen, token);
    });
    socket.onClose((info) => {
      if (gen !== this.generation) return;
      this.onSocketClosed(`${info.code} ${info.reason}`.trim());
    });
  }

  /**
   * A non-retryable failure detected on an open socket (subprotocol mismatch):
   * surface a typed error, close the socket, and do NOT reconnect. Mirrors the
   * AUTH_FAILED/KICKED fatal policy — the `fatalDetail` flag makes the ensuing
   * onClose skip the reconnect path.
   */
  private failFatal(socket: SyncSocket, err: SubprotocolError): void {
    this.fatalDetail = err.message;
    this.setStatus("error", err.message);
    socket.close(1002, "unsupported subprotocol");
  }

  private onSocketClosed(detail: string): void {
    this.connecting = false;
    this.authed = false;
    this.socket = null;
    this.refreshGen++; // cancel any pending refresh
    this.probeNonce = null; // the socket a probe was doubting is gone
    const lived = this.clock.now() - this.connectedAt;
    if (this.connectedAt > 0 && lived >= this.tuning.backoffResetMs) {
      this.attempt = 0;
    }
    if (this.closed) return;
    if (this.fatalDetail !== null) {
      // A fatal condition already surfaced its error; never auto-reconnect.
      this.setStatus("error", this.fatalDetail);
      return;
    }

    switch (this.lastClose) {
      case CloseReason.AUTH_FAILED:
      case CloseReason.KICKED:
        // Fatal: bad credentials or membership revoked — do not reconnect.
        this.setStatus("error", detail || "connection closed");
        return;
      case CloseReason.TOKEN_EXPIRED:
        // Seamless: mint a fresh token and reconnect immediately.
        this.attempt = 0;
        this.startConnect();
        return;
      default:
        this.scheduleReconnect(detail || "connection lost");
    }
  }

  private scheduleReconnect(detail: string): void {
    if (this.closed) return;
    this.setStatus("offline", detail);
    const delay = fullJitterBackoff(
      this.attempt,
      this.tuning.backoffBaseMs,
      this.tuning.backoffCapMs,
      this.tuning.jitter,
    );
    this.attempt++;
    const gen = this.generation;
    void this.clock.timer(delay).then(() => {
      if (this.closed || gen !== this.generation) return;
      this.startConnect();
    });
  }

  private handleServer(frame: Uint8Array, gen: number, token: string): void {
    let msg: ServerMessage;
    try {
      msg = ServerMessage.decode(frame);
    } catch {
      return; // undecodable frame; ignore
    }
    if (msg.authAck) {
      this.authed = true;
      this.connecting = false;
      this.attempt = 0;
      this.connectedAt = this.clock.now();
      this.role = msg.authAck.role;
      this.userId = msg.authAck.userId;
      this.setStatus("live");
      this.cb.onAuthAck(msg.authAck.userId, msg.authAck.role);
      this.scheduleRefresh(token, gen);
      this.resubscribeAll();
      return;
    }
    if (msg.subscribeAck) {
      const a = msg.subscribeAck;
      this.cb.onSubscribeAck(a.docId, a.epoch, a.epochChanged);
      return;
    }
    if (msg.doc) {
      this.cb.onDocPayload(msg.doc.docId, msg.doc.payload);
      return;
    }
    if (msg.rejected) {
      this.cb.onRejected(msg.rejected.docId, msg.rejected.reason, msg.rejected.detail);
      return;
    }
    if (msg.event) {
      const e = msg.event;
      if (e.docCreated) this.cb.onDocCreated(e.docCreated.docId);
      else if (e.docRemoved) this.cb.onDocRemoved(e.docRemoved.docId);
      else if (e.roleChanged) {
        this.role = e.roleChanged.role;
        this.cb.onRoleChanged(e.roleChanged.role);
      }
      return;
    }
    if (msg.close) {
      // Server-initiated close: capture the reason so onSocketClosed picks
      // the right reconnect policy, then let the socket close event fire.
      this.lastClose = msg.close.reason;
      return;
    }
    if (msg.pong) {
      // Pongs carry no state; the ONLY thing tracked is whether one answered
      // the outstanding `verifyLiveness()` probe. Nonce-matched, so a Pong for
      // an older probe (or an unsolicited one) cannot vouch for this socket.
      if (this.probeNonce !== null && msg.pong.nonce === this.probeNonce) {
        this.probeNonce = null;
      }
      return;
    }
  }

  /** Re-mint the token at 80% of its TTL and reconnect seamlessly. */
  private scheduleRefresh(token: string, gen: number): void {
    const expMs = tokenExpiryMs(token);
    if (expMs === null) return;
    const ttl = expMs - this.clock.now();
    if (ttl <= 0) return;
    const refreshIn = Math.floor(ttl * this.tuning.refreshAtFraction);
    const myRefreshGen = ++this.refreshGen;
    void this.clock.timer(refreshIn).then(() => {
      if (
        this.closed ||
        gen !== this.generation ||
        myRefreshGen !== this.refreshGen ||
        !this.authed
      ) {
        return;
      }
      this.replaceSocket();
    });
  }

  /**
   * Close-then-reconnect (decisions/20): drop the current socket BEFORE opening
   * its replacement so we never linger with two authenticated connections, and
   * reconnect immediately rather than through backoff — the socket is being
   * replaced on purpose, not after a failure to reach the server.
   * `startConnect` bumps the generation, so the old socket's onClose is ignored
   * rather than triggering a competing backoff reconnect. CRDT state resyncs on
   * the new socket; no data is lost.
   *
   * Shared by the proactive token refresh and the half-open teardown in
   * `verifyLiveness()` — one teardown path, not two.
   */
  private replaceSocket(): void {
    this.attempt = 0;
    this.probeNonce = null;
    this.socket?.close();
    this.socket = null;
    this.authed = false;
    this.startConnect();
  }

  private queueSubscribe(docId: string): void {
    if (!this.pendingSubs.includes(docId)) this.pendingSubs.push(docId);
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    queueMicrotask(() => {
      this.flushScheduled = false;
      const batch = this.pendingSubs;
      this.pendingSubs = [];
      this.flushSubscribes(batch);
    });
  }

  /** On (re)connect, resubscribe the entire desired set with fresh SVs. */
  private resubscribeAll(): void {
    this.pendingSubs = [];
    this.flushSubscribes([...this.desired.keys()]);
  }

  private flushSubscribes(docIds: string[]): void {
    if (!this.authed || docIds.length === 0) return;
    const subs: Subscription[] = [];
    for (const docId of docIds) {
      const mode = this.desired.get(docId);
      if (mode === undefined) continue;
      const { stateVector, knownEpoch } = this.cb.subscribeParams(docId);
      subs.push({ docId, mode, knownEpoch, stateVector });
    }
    // Respect the 512-per-batch cap (protocol spec limits table).
    for (let i = 0; i < subs.length; i += MAX_BATCH) {
      this.sendMessage({ subscribe: { subs: subs.slice(i, i + MAX_BATCH) } });
    }
  }

  private sendMessage(msg: ClientMessage): void {
    const socket = this.socket;
    if (!socket) return;
    this.rawSend(socket, msg);
  }

  private rawSend(socket: SyncSocket, msg: ClientMessage): void {
    socket.send(ClientMessage.encode(msg).finish());
  }
}
